import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDoc, getDocs, doc, collection, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { getUserRoles } from '../../utils/roleHelpers';
import Button from '../shared/UI/Button';
import { formatDateTime, getFriendlyError } from '../../utils/formatters';
import SummaryChart from './SummaryChart';

// ─── Markdown styles injected once ────────────────────────────────────────────
const markdownStyles = `
.ai-summary-content { font-size: 14px; line-height: 1.75; color: var(--color-text); }
.ai-summary-content h1,.ai-summary-content h2,.ai-summary-content h3 {
  color: var(--color-text); font-weight: 700; margin: 16px 0 8px;
}
.ai-summary-content h1 { font-size: 17px; }
.ai-summary-content h2 { font-size: 15px; }
.ai-summary-content h3 { font-size: 14px; }
.ai-summary-content p { margin: 0 0 12px; }
.ai-summary-content ul, .ai-summary-content ol { padding-left: 20px; margin: 0 0 12px; }
.ai-summary-content li { margin-bottom: 4px; }
.ai-summary-content strong { font-weight: 600; color: var(--color-text); }
.ai-summary-content hr { border: none; border-top: 1px solid var(--color-border); margin: 16px 0; }
.ai-summary-content table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 13px; }
.ai-summary-content th { background: var(--color-border-light); padding: 6px 10px; text-align: left; font-weight: 600; border: 1px solid var(--color-border); }
.ai-summary-content td { padding: 6px 10px; border: 1px solid var(--color-border); }
.ai-summary-content tr:nth-child(even) td { background: var(--color-border-light); }
.ai-summary-content tr.totals-row td { background: var(--totals-bg, #f0f0f0) !important; font-weight: 600; }
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = markdownStyles;
  document.head.appendChild(style);
  stylesInjected = true;
}

// ─── Totals-row colour helper ─────────────────────────────────────────────────
function extractText(node) {
  if (!node) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node.props && node.props.children) return extractText(node.props.children);
  return '';
}

const markdownComponents = {
  table: ({ node, ...props }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', margin: '12px 0', fontSize: 13 }} {...props} />
  ),
  th: ({ node, ...props }) => (
    <th style={{ background: 'var(--color-border-light)', padding: '6px 10px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--color-border)' }} {...props} />
  ),
  td: ({ node, ...props }) => (
    <td style={{ padding: '6px 10px', border: '1px solid var(--color-border)' }} {...props} />
  ),
  tr: ({ node, children, ...props }) => {
    const cells = React.Children.toArray(children);
    const cellTexts = cells.map(cell => extractText(cell));
    const firstCellText = cellTexts[0] || '';
    const allCellText = cellTexts.join(' ');
    const isTotalsRow = (
      firstCellText.trim() === '' ||
      firstCellText.includes('Total Score') ||
      cellTexts.some(t =>
        t.includes('Scores extremely high') ||
        t.includes('Scores well') ||
        t.includes('Scores moderately') ||
        t.includes('Scores below') ||
        t.includes('Advance for screening') ||
        t.includes('Below threshold') ||
        t.includes('Further information')
      )
    );
    let bgColor = 'transparent';
    let textColor = 'inherit';
    if (allCellText.includes('extremely high') ||
        allCellText.includes('Advance for screening') ||
        allCellText.includes('🟢')) {
      bgColor = '#d4edda';
      textColor = '#155724';
    } else if (allCellText.includes('Scores well') ||
               allCellText.includes('Scores moderately') ||
               allCellText.includes('🟡')) {
      bgColor = '#fff3cd';
      textColor = '#856404';
    } else if (allCellText.includes('below expectations') ||
               allCellText.includes('Below threshold') ||
               allCellText.includes('not recommended') ||
               allCellText.includes('🔴')) {
      bgColor = '#f8d7da';
      textColor = '#721c24';
    } else if (isTotalsRow) {
      bgColor = '#f5f5f0';
      textColor = '#1a1a1a';
    }
    if (bgColor !== 'transparent') {
      return <tr className="totals-row" style={{'--totals-bg': bgColor, color: textColor}} {...props}>{children}</tr>;
    }
    return <tr {...props}>{children}</tr>;
  },
};

// ─── Signature block ─────────────────────────────────────────────────────────

const sigThStyle = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 600,
  border: '1px solid #e8e8e0', color: '#1a1a1a',
};
const sigTdStyle = {
  padding: '8px 12px', border: '1px solid #e8e8e0', verticalAlign: 'top',
};

function SignatureBlock({ dealId, submission }) {
  const [signatures, setSignatures] = React.useState([]);

  React.useEffect(() => {
    if (!dealId) return;
    const fetchSignatures = async () => {
      try {
        const sigs = [];

        const confirmedName = submission?.ai_summary_confirmed_by_name ||
                              submission?.ai_summary_confirmed_by;
        if (confirmedName) {
          sigs.push({
            role: 'Primary Analyst',
            name: confirmedName,
            action: 'AI Summary Confirmed',
            date: submission?.ai_summary_confirmed_at,
          });
        }

        const roleMap = {
          primary_analyst: 'Primary Analyst',
          analyst_review: 'Primary Analyst',
          peer_review: 'Peer Reviewer',
          hop_review: 'Head of Pipeline',
          iic_approval: 'IIC Approver',
          cio_review: 'CIO',
          md_review: 'Managing Director',
        };
        const fmtOutcome = (v) => {
          if (!v) return 'Reviewed';
          return v.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        };

        const reviewsSnap = await getDocs(
          query(
            collection(db, 'deals', dealId, 'analyst_reviews'),
            orderBy('created_at', 'asc')
          )
        );

        reviewsSnap.docs.forEach(reviewDoc => {
          const review = reviewDoc.data();
          const reviewerName = review.performed_by_name || review.reviewer_name;
          if (reviewerName) {
            sigs.push({
              role: roleMap[review.review_type] || 'Reviewer',
              name: reviewerName,
              action: fmtOutcome(review.outcome),
              date: review.created_at || review.completed_at,
            });
          }
        });

        const iicSnap = await getDocs(
          collection(db, 'deals', dealId, 'iic_approvals')
        );
        const iicDocs = iicSnap.docs
          .map(d => d.data())
          .filter(a => a.status !== 'pending' && (a.performed_by_name || a.approved_by_name || a.reviewer_name))
          .sort((a, b) => {
            const toMs = v => v?.toDate ? v.toDate().getTime() : new Date(v || 0).getTime();
            return toMs(a.decided_at) - toMs(b.decided_at);
          });
        iicDocs.forEach(approval => {
          sigs.push({
            role: 'IIC Approver',
            name: approval.performed_by_name || approval.approved_by_name || approval.reviewer_name,
            action: fmtOutcome(approval.outcome || approval.decision || approval.status),
            date: approval.decided_at || approval.created_at,
          });
        });

        setSignatures(sigs);
      } catch (err) {
        console.error('SignatureBlock error:', err);
      }
    };
    fetchSignatures();
  }, [dealId, submission?.id, submission?.ai_summary_confirmed_at, submission?.ai_summary_confirmed_by, submission?.ai_summary_confirmed_by_name]);

  if (!signatures.length) return null;

  const formatSigDate = (ts) => {
    if (!ts) return '—';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };

  return (
    <div style={{ marginTop: 32, borderTop: '2px solid #1e3a5f', paddingTop: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e3a5f', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Approvals and Sign-Off
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f5f5f0' }}>
            <th style={sigThStyle}>Role</th>
            <th style={sigThStyle}>Name</th>
            <th style={sigThStyle}>Decision</th>
            <th style={sigThStyle}>Date</th>
          </tr>
        </thead>
        <tbody>
          {signatures.map((sig, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafaf7' }}>
              <td style={sigTdStyle}>{sig.role}</td>
              <td style={sigTdStyle}>{sig.name}</td>
              <td style={sigTdStyle}>{sig.action}</td>
              <td style={sigTdStyle}>{formatSigDate(sig.date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 24, fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>
        This document was generated by the Mirepa Investment Advisors Deal Origination Portal.
        Electronic approvals recorded above are binding as per the firm's approval policy.
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AISummaryPanel({ submission, dealId, onConfirmed }) {
  injectStyles();
  const { userProfile } = useAuth();
  const [mode, setMode] = useState('view'); // 'view' | 'editing'
  const [editedText, setEditedText] = useState('');

  // Confirm state
  const [confirming, setIsConfirming] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  // Regenerate state
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);
  const [regenerateSuccess, setRegenerateSuccess] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  const [showOriginal, setShowOriginal] = useState(false);
  const [dealData, setDealData] = useState(null);
  const [fundShortName, setFundShortName] = useState('');
  const [anySubmissionConfirmed, setAnySubmissionConfirmed] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    getDoc(doc(db, 'deals', dealId)).then(dealDoc => {
      if (dealDoc.exists()) {
        const d = dealDoc.data();
        setDealData(d);
        if (d.fund_id) {
          getDoc(doc(db, 'funds', d.fund_id)).then(fundDoc => {
            if (fundDoc.exists()) {
              setFundShortName(fundDoc.data().short_name || fundDoc.data().name || 'MIA');
            }
          }).catch(() => {});
        }
      }
    }).catch(() => {});
  }, [dealId]);

  // Safety-net: if the prop submission is not the confirmed one (is_current out of
  // sync), check whether any submission in this deal has been confirmed.
  useEffect(() => {
    if (!dealId) return;
    getDocs(collection(db, 'deals', dealId, 'pif_submissions'))
      .then(snap => {
        setAnySubmissionConfirmed(snap.docs.some(d => d.data().ai_summary_confirmed === true));
      })
      .catch(() => {});
  }, [dealId]);

  if (!submission) return null;

  const {
    ai_summary,
    ai_summary_confirmed,
    ai_summary_confirmed_at,
    ai_summary_confirmed_by_name,
    ai_summary_confirmed_by,
    ai_summary_final,
    ai_summary_original,
  } = submission;

  const displaySummary = ai_summary_final || ai_summary || '';

  const userRoles = getUserRoles(userProfile);
  const canConfirm = userRoles.includes('analyst') || userRoles.includes('head_of_pipeline');
  const isAlreadyConfirmed = !!ai_summary_confirmed || anySubmissionConfirmed;

  const handleRegenerate = async () => {
    setShowRegenerateDialog(false);
    try {
      setRegenerating(true);
      setRegenerateError(null);
      setRegenerateSuccess(false);

      console.log('Regenerate Summary: calling generateAISummary', {
        dealId,
        pifSubmissionId: submission.id,
      });

      const fns = getFunctions();
      const regenerateFn = httpsCallable(fns, 'generateAISummary');
      await regenerateFn({ dealId, pifSubmissionId: submission.id });

      console.log('Regenerate Summary: success');
      setRegenerateSuccess(true);

      if (onConfirmed) onConfirmed();

    } catch (error) {
      console.error('Regenerate Summary: error caught:', error);
      setRegenerateError(getFriendlyError(error));

    } finally {
      setRegenerating(false);
    }
  };

  if (!ai_summary && !ai_summary_final) {
    return (
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <div style={{
          padding: '10px 14px',
          background: regenerating ? 'var(--color-info-bg)' : 'var(--color-warning-bg)',
          borderBottom: regenerateError ? '1px solid var(--color-border)' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 8,
        }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: regenerating ? 'var(--color-info)' : 'var(--color-warning)' }}>
            {regenerating
              ? '⏳ Generating AI summary, please wait…'
              : '⚠ AI summary has not been generated yet.'}
          </p>
          {!regenerating && canConfirm && (
            <Button size="sm" variant="secondary" onClick={handleRegenerate}>
              Generate Summary
            </Button>
          )}
        </div>
        {regenerateError && (
          <div style={{ padding: '8px 14px', background: 'var(--color-error-bg)' }}>
            <p style={{ fontSize: 13, color: 'var(--color-error)' }}>{regenerateError}</p>
          </div>
        )}
      </div>
    );
  }

  // ── Editing mode ──────────────────────────────────────────────────────────
  if (mode === 'editing') {
    return (
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', background: 'var(--color-info-bg)', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-info)' }}>
            ✏ Editing AI Summary — your changes will be stored alongside the original
          </p>
        </div>
        <div style={{ padding: 14 }}>
          <textarea
            value={editedText}
            onChange={e => setEditedText(e.target.value)}
            style={{
              width: '100%',
              minHeight: 320,
              padding: '10px 12px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              fontSize: 13,
              fontFamily: 'monospace',
              lineHeight: 1.6,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 6 }}>
            Tip: Markdown supported. Use **bold**, ## headings, - bullet points.
          </p>
        </div>
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--color-border)', background: 'var(--color-border-light)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={() => { setMode('view'); setEditedText(''); }}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={() => setMode('view')}>
            Save Draft
          </Button>
        </div>
      </div>
    );
  }

  // ── Confirm handler ────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    try {
      setIsConfirming(true);
      setConfirmError(null);

      console.log('Confirm Summary: calling Cloud Function', {
        dealId,
        submissionId: submission.id,
      });

      const fns = getFunctions();
      const confirmFn = httpsCallable(fns, 'confirmAISummary');

      const result = await confirmFn({
        dealId,
        submissionId: submission.id,
        editedSummary: editedText.trim() || displaySummary,
      });

      console.log('Confirm Summary: success, staying on page', result);
      setConfirmSuccess(true);

      // Notify parent to refresh data (does not navigate)
      if (onConfirmed) onConfirmed();

      // Explicitly stay on page — no navigation
      return;

    } catch (error) {
      console.error('Confirm Summary: error caught:', error);
      console.log('Confirm Summary: error code:', error.code);
      console.log('Confirm Summary: error message:', error.message);
      setConfirmError(getFriendlyError(error));
      // Explicitly stay on page — no navigation
      return;

    } finally {
      setIsConfirming(false);
    }
  };

  // ── Chart-aware renderer ───────────────────────────────────────────────────
  function renderWithCharts(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let markdownBuffer = [];

    lines.forEach((line, index) => {
      if (line.trim().startsWith('CHART_DATA:')) {
        if (markdownBuffer.length > 0) {
          elements.push(
            <ReactMarkdown key={'md-' + index} remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {markdownBuffer.join('\n')}
            </ReactMarkdown>
          );
          markdownBuffer = [];
        }
        try {
          const jsonStr = line.trim().replace('CHART_DATA:', '').trim();
          const chartData = JSON.parse(jsonStr);
          elements.push(<SummaryChart key={'chart-' + index} {...chartData} />);
        } catch (e) {
          console.warn('Chart parse error:', e.message);
        }
      } else {
        markdownBuffer.push(line);
      }
    });

    if (markdownBuffer.length > 0) {
      elements.push(
        <ReactMarkdown key="md-final" remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {markdownBuffer.join('\n')}
        </ReactMarkdown>
      );
    }

    return <div>{elements}</div>;
  }

  // ── PDF download ─────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const reportElement = document.getElementById('ai-summary-report-content');
      if (!reportElement) {
        alert('Could not find report content. Please try again.');
        return;
      }
      const today = new Date();
      const dateStr = today.getFullYear().toString() +
        String(today.getMonth() + 1).padStart(2, '0') +
        String(today.getDate()).padStart(2, '0');
      const cleanCompanyName = (dealData?.company_name || 'Company').replace(/[^a-zA-Z0-9]/g, '');
      const cleanFundName = fundShortName.replace(/[^a-zA-Z0-9]/g, '') || 'MIA';
      const filename = `MIA_${cleanFundName}_PIFReport_${cleanCompanyName}_${dateStr}_v1.0.pdf`;
      const options = {
        margin: [15, 15, 15, 15],
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      await html2pdf().set(options).from(reportElement).save();
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Could not generate PDF. Please try again.');
    }
  };

  // ── View mode ──────────────────────────────────────────────────────────────
  const confirmedName = ai_summary_confirmed_by_name || ai_summary_confirmed_by || 'analyst';

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>

      {/* Regenerate confirmation dialog */}
      {showRegenerateDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: 'var(--color-card)', borderRadius: 'var(--radius-lg)',
            padding: 24, maxWidth: 440, width: '100%', margin: 16,
            boxShadow: 'var(--shadow-md)',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Regenerate AI Summary?</h3>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              This will replace the current summary with a new one generated by Claude.
              The current summary will be lost if it has not been confirmed. Continue?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button size="sm" variant="secondary" onClick={() => setShowRegenerateDialog(false)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={handleRegenerate}>
                Regenerate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '10px 14px',
        background: isAlreadyConfirmed ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: isAlreadyConfirmed ? 'var(--color-success)' : 'var(--color-warning)' }}>
          {isAlreadyConfirmed
            ? `✓ AI Summary confirmed by ${confirmedName} on ${formatDateTime(ai_summary_confirmed_at)}`
            : '⚠ AI-Generated Summary — Pending Analyst Confirmation'
          }
        </p>
        {!isAlreadyConfirmed && canConfirm && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Button
              size="sm"
              variant="secondary"
              loading={regenerating}
              disabled={confirming}
              onClick={() => setShowRegenerateDialog(true)}
            >
              Regenerate
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={confirming || regenerating}
              onClick={() => {
                setEditedText(displaySummary);
                setMode('editing');
              }}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={confirming}
              disabled={regenerating}
              onClick={handleConfirm}
            >
              Confirm Summary
            </Button>
          </div>
        )}
        {isAlreadyConfirmed && (
          <button
            onClick={handleDownloadPDF}
            style={{
              padding: '7px 14px', borderRadius: 6, border: '1px solid #1e3a5f',
              background: '#1e3a5f', color: '#fff', cursor: 'pointer',
              fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            ↓ Download PDF
          </button>
        )}
      </div>

      {/* Regenerating indicator */}
      {regenerating && (
        <div style={{ padding: '8px 14px', background: 'var(--color-info-bg)', borderBottom: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: 13, color: 'var(--color-info)' }}>Generating new summary…</p>
        </div>
      )}

      {/* Regenerate success */}
      {regenerateSuccess && !regenerating && (
        <div style={{ padding: '8px 14px', background: 'var(--color-success-bg)', borderBottom: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: 13, color: 'var(--color-success)', fontWeight: 600 }}>
            ✓ Summary regenerated. The new summary has been saved.
          </p>
        </div>
      )}

      {/* Regenerate error */}
      {regenerateError && (
        <div style={{ padding: '8px 14px', background: 'var(--color-error-bg)', borderBottom: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: 13, color: 'var(--color-error)' }}>{regenerateError}</p>
        </div>
      )}

      {/* Confirm success */}
      {confirmSuccess && (
        <div style={{ padding: '8px 14px', background: 'var(--color-success-bg)', borderBottom: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: 13, color: 'var(--color-success)', fontWeight: 600 }}>
            ✓ Summary confirmed successfully.
          </p>
        </div>
      )}

      {/* Confirm error */}
      {confirmError && (
        <div style={{ padding: '8px 14px', background: 'var(--color-error-bg)', borderBottom: '1px solid var(--color-border)' }}>
          <p style={{ fontSize: 13, color: 'var(--color-error)' }}>{confirmError}</p>
        </div>
      )}

      {/* Summary body */}
      <div style={{ padding: '14px 16px', background: 'var(--color-card)' }}>
        <div id="ai-summary-report-content" className="ai-summary-content">
          {renderWithCharts(editedText.trim() ? editedText : displaySummary)}
          {isAlreadyConfirmed && (
            <SignatureBlock dealId={dealId} submission={submission} />
          )}
        </div>
      </div>

      {/* Original summary collapsible */}
      {isAlreadyConfirmed && ai_summary_original && ai_summary_final && (
        <div style={{ borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={() => setShowOriginal(s => !s)}
            style={{ width: '100%', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 12, color: 'var(--color-text-secondary)' }}
          >
            {showOriginal ? '▲ Hide' : '▼ View'} original AI-generated summary
          </button>
          {showOriginal && (
            <div style={{ padding: '0 16px 14px', background: 'var(--color-border-light)' }}>
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8, fontStyle: 'italic' }}>
                Original Claude-generated text before analyst edits:
              </p>
              <div className="ai-summary-content" style={{ fontSize: 13 }}>
                {renderWithCharts(ai_summary_original)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
